import { Link } from 'react-router-dom'
import { CalendarDays, GraduationCap, Loader2, ArrowRight } from 'lucide-react'
import { useLocale } from '../i18n/LocaleContext'
import { useCamps } from '../hooks/useCamps'
import { getCampPath } from '../utils/routes'
import SectionHeading from '../components/ui/SectionHeading'
import AvailabilityBadge from '../components/ui/AvailabilityBadge'
import PlaceholderImage from '../components/ui/PlaceholderImage'
import Newsletter from '../components/Newsletter'
import SEOHead from '../components/SEOHead'

export default function CampsIndex() {
  const { locale, t } = useLocale()
  const { camps, loading } = useCamps()
  const activeCamps = camps.filter((c) => c.active)

  return (
    <>
      <SEOHead title={t.camps.metaTitle} description={t.camps.metaDescription} />

      {/* Evergreen intro — the always-present SEO content */}
      <section className="section-padding bg-surface">
        <div className="container-max">
          <SectionHeading title={t.camps.title} subtitle={t.camps.subtitle} />
          <p className="max-w-3xl mx-auto text-center text-text-secondary leading-relaxed">
            {t.camps.intro}
          </p>
        </div>
      </section>

      {/* Camp list or coming-soon + newsletter */}
      <section className="section-padding bg-white">
        <div className="container-max">
          {loading && activeCamps.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin text-primary" size={32} aria-label="Loading" />
            </div>
          ) : activeCamps.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {activeCamps.map((camp) => (
                <Link
                  key={camp.id}
                  to={getCampPath(locale, camp.id)}
                  className="group flex flex-col overflow-hidden rounded-card border border-border transition-shadow hover:shadow-card"
                >
                  {camp.image && !camp.image.includes('placeholder') ? (
                    <img
                      src={camp.image}
                      alt={camp.title[locale]}
                      width={800}
                      height={600}
                      loading="lazy"
                      className="w-full aspect-[4/3] object-cover"
                    />
                  ) : (
                    <PlaceholderImage label={camp.title[locale]} variant="camp" className="w-full aspect-[4/3]" />
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <h3 className="font-heading font-semibold text-lg text-secondary">
                        {camp.title[locale]}
                      </h3>
                      <AvailabilityBadge availability={camp.availability} locale={locale} />
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="inline-flex items-center gap-1 text-xs text-text-secondary bg-surface px-2 py-1 rounded-full">
                        <CalendarDays size={12} />
                        {camp.dates[locale]}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-text-secondary bg-surface px-2 py-1 rounded-full">
                        <GraduationCap size={12} />
                        {camp.category[locale]}
                      </span>
                    </div>
                    <p className="text-sm text-text-secondary line-clamp-2 mb-4">
                      {camp.shortDescription[locale]}
                    </p>
                    <div className="mt-auto flex items-center justify-between">
                      <span className="text-primary font-semibold">{camp.price}</span>
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-primary group-hover:gap-2 transition-all">
                        {t.camps.viewDetails}
                        <ArrowRight size={16} />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="max-w-xl mx-auto text-center bg-surface rounded-card p-8 md:p-12">
              <h2 className="font-heading font-semibold text-2xl text-secondary mb-3">
                {t.camps.comingSoonTitle}
              </h2>
              <p className="text-text-secondary mb-6">{t.camps.comingSoonText}</p>
              <Newsletter />
            </div>
          )}
        </div>
      </section>
    </>
  )
}
